import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { activeHostBrand } from '@/components/HostBranding';
import { readableTextOn } from '@/lib/brandColor';
import { refreshCatalogScope } from '@/lib/catalogScope';

/** managed_consent_state() for the signed-in user on a branded host. */
export interface ManagedConsentState {
  managed: boolean;
  display_name?: string;
  role?: 'owner' | 'admin' | 'member' | null;
  member_status?: 'pending' | 'active' | 'suspended';
  claim?: boolean;
  oversight?: boolean;
  consented?: boolean;
  existing_projects?: number;
}

/**
 * First entry into a partner-managed organisation: the person is told, before
 * any work, that the organisation sees their projects and activity, and
 * accepts our terms (a partner sign-in skips our own sign-up). An existing
 * private account joining here keeps its earlier projects private unless the
 * person ticks the box. Backed by accept_managed_consent().
 */
export default function ManagedConsentScreen({
  host, state, onAccepted, onSignOut,
}: {
  host: string;
  state: ManagedConsentState;
  onAccepted: () => void;
  onSignOut: () => void;
}) {
  const { t, i18n } = useTranslation('dashboard');
  const [terms, setTerms] = useState(false);
  const [prior, setPrior] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const brand = activeHostBrand;
  const color = brand?.primaryColor && /^#[0-9a-f]{6}$/i.test(brand.primaryColor) ? brand.primaryColor : '#1d4ed8';
  // Global button styles override utility text colours; set it inline.
  const onColor = readableTextOn(color);
  const org = state.display_name ?? brand?.name ?? '';
  const existing = state.existing_projects ?? 0;
  const lang = i18n.language === 'en' ? '' : `/${i18n.language}`;

  const accept = async () => {
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.rpc('accept_managed_consent' as never, { p_host: host, p_include_prior: prior } as never);
    setBusy(false);
    if (err) { setError(t('managed_consent_error', { defaultValue: 'Your answer could not be saved. Please try again.' })); return; }
    void refreshCatalogScope();
    onAccepted();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="max-w-lg w-full border rounded-lg p-8" style={{ borderColor: 'var(--color-gray-border, #e5e7eb)' }}>
        {brand?.logoUrl && <img src={brand.logoUrl} alt="" className="h-10 mb-5 object-contain" />}
        <h1 className="text-lg font-semibold mb-3" style={{ color }}>
          {state.claim
            ? t('managed_consent_claim_title', { defaultValue: 'You are joining {{org}}', org })
            : t('managed_consent_title', { defaultValue: 'Account managed by {{org}}', org })}
        </h1>
        <p className="text-sm text-neutral-700 dark:text-neutral-300 mb-3">
          {state.claim
            ? t('managed_consent_claim_body', { defaultValue: 'You are signing in with an account you already had. After joining, {{org}} will see the projects you create from now on and a record of your work in this organisation: sign-ins, project changes and exports. The view is read-only.', org })
            : t('managed_consent_body', { defaultValue: '{{org}} sees every project you create in this planner and a record of your work: sign-ins, project changes and exports. The view is read-only.', org })}
        </p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-5">
          {t('managed_consent_controller', { defaultValue: 'For this oversight, {{org}} is the controller of your data and CCTVplanner.io processes it on its behalf.', org })}
        </p>

        {existing > 0 && (
          <label htmlFor="managed-consent-prior" className="flex items-start gap-2 text-sm mb-1 cursor-pointer">
            <input id="managed-consent-prior" aria-label={t('managed_consent_prior', { defaultValue: 'Also show {{org}} my earlier projects ({{n}})', org, n: existing })} type="checkbox" className="mt-1" checked={prior} onChange={(e) => setPrior(e.target.checked)} />
            <span>{t('managed_consent_prior', { defaultValue: 'Also show {{org}} my earlier projects ({{n}})', org, n: existing })}</span>
          </label>
        )}
        {existing > 0 && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4 ml-6">
            {t('managed_consent_prior_note', { defaultValue: 'Left unticked, your earlier projects stay visible to you only.' })}
          </p>
        )}

        <label htmlFor="managed-consent-terms" className="flex items-start gap-2 text-sm mb-6 cursor-pointer">
          <input id="managed-consent-terms" aria-label={t('managed_consent_terms', { defaultValue: 'I accept the CCTVplanner.io terms of service and privacy policy.' })} type="checkbox" className="mt-1" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
          <span>
            {t('managed_consent_terms', { defaultValue: 'I accept the CCTVplanner.io terms of service and privacy policy.' })}{' '}
            <a href={`https://cctvplanner.io${lang}/terms`} target="_blank" rel="noopener noreferrer" className="underline">
              {t('managed_consent_terms_link', { defaultValue: 'Terms' })}
            </a>
            {' · '}
            <a href={`https://cctvplanner.io${lang}/rodo`} target="_blank" rel="noopener noreferrer" className="underline">
              {t('managed_consent_privacy_link', { defaultValue: 'Privacy policy' })}
            </a>
          </span>
        </label>

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={accept}
            disabled={!terms || busy}
            className="px-4 py-2 text-sm rounded disabled:opacity-50 inline-flex items-center gap-2"
            style={{ background: color, color: onColor }}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('managed_consent_accept', { defaultValue: 'Accept and continue' })}
          </button>
          <button onClick={onSignOut} className="px-4 py-2 text-sm rounded border" style={{ borderColor: 'var(--color-gray-border, #e5e7eb)' }}>
            {t('sign_out', { defaultValue: 'Sign out' })}
          </button>
        </div>
      </div>
    </div>
  );
}
